// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { routes } from "../../config/constant";
import { RootState, useAppDispatch, useAppSelector } from "../../slices/store";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Stack } from "@mui/system";
import { RatingForm } from "./RatingForm";
import { useEffect } from "react";
import { setOngoingExamStatus } from "../../slices/examSlice/ongoingExam";
import { finishPracticalExam } from "../../slices/examSlice/submitAnswerSheet";
import { LoadingButton } from "@mui/lab";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  ".MuiBackdrop-root": {
    backdropFilter: "blur(10px)",
    backgroundColor: "rgba(0,0,30,0.4)",
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

export default function PracticalExamSubmissionDialog(paperDetails: any) {
  const examState = useAppSelector((state: RootState) => state.selectedExam);
  const practicalExamState = useAppSelector(
    (state: RootState) => state.examResults
  );
  const ongoingExamState = useAppSelector(
    (state: RootState) => state.ongoingExam
  );

  const dispatch = useAppDispatch();
  let navigate = useNavigate();

  const selectedExam = examState.response[0];
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    if (ongoingExamState.state === "completed") {
      submitExam();
    }
  }, [ongoingExamState.state]);

  const submitExam = () => {
    dispatch(
      finishPracticalExam({
        examId: selectedExam?.id,
        paperDetails: paperDetails.paperDetails,
      })
    );
    setOpen(true);
  };
  const handleClose = () => {
    dispatch(setOngoingExamStatus("idle"));
    setOpen(false);
    navigate(routes.exams);
  };

  return (
    <div>
      <LoadingButton
        loading={practicalExamState.state === "loading"}
        sx={{ my: 2 }}
        variant="contained"
        onClick={submitExam}
        translate="no"
      >
        Finish Exam
      </LoadingButton>
      {practicalExamState.state === "success" && (
        <BootstrapDialog
          aria-labelledby="customized-dialog-title"
          open={open}
          maxWidth={"md"}
        >
          <BootstrapDialogTitle
            id="customized-dialog-title"
            onClose={handleClose}
          >
            {`${
              selectedExam?.examVisibleType
                ? selectedExam?.examVisibleType
                : selectedExam?.examType
            } - ${selectedExam?.examPaperType}`}
          </BootstrapDialogTitle>
          <DialogContent dividers>
            <Box sx={{ m: 3 }}>
              <Stack
                alignItems={"center"}
                justifyContent={"center"}
                direction="row"
                spacing={2}
                sx={{ mb: 3 }}
              >
                <CheckCircleIcon
                  sx={{ color: "secondary.main", fontSize: "30px" }}
                />
                <Typography
                  sx={{ fontWeight: "bold", mb: 3 }}
                  gutterBottom
                  color={"secondary.main"}
                  variant="h6"
                >
                  You have completed your exam!
                </Typography>
              </Stack>

              <ul style={{ marginBottom: "30px" }}>
                <li>
                  <Typography variant="subtitle2">
                    Submit the webcam input and screen recordings along with the
                    configuration files and answer sheet within 24 hours of
                    completing the exam.
                  </Typography>
                </li>
                <li>
                  <Typography variant="subtitle2">
                    Failure to submit the recording within the specified time
                    may result in a failing grade or prevent you from attempting
                    any WSO2 certifications.
                  </Typography>
                </li>
                <li>
                  <Typography variant="subtitle2">
                    Do not share the screen recordings with anyone other than
                    WSO2.
                  </Typography>
                </li>
                <li>
                  <Typography variant="subtitle2">
                    Your exam results will be sent to you via email within 14-18
                    business days.
                  </Typography>
                </li>
              </ul>

              <RatingForm
                certificationType={
                  selectedExam?.examVisibleType
                    ? selectedExam?.examVisibleType
                    : selectedExam?.examType
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              sx={{ px: 1, color: "secondary.main" }}
              autoFocus
              onClick={handleClose}
            >
              Done
            </Button>
          </DialogActions>
        </BootstrapDialog>
      )}
    </div>
  );
}
